import type {
  EnablementCondition,
  QuestionnaireAnswerValue,
  QuestionnaireCondition,
  QuestionnaireDefinitionDto,
} from "@repo/data-store";
import type { QuestionnaireScoreCalculation } from "@repo/domain";
import { implicitTotalScore, TOTAL_SCORE_ID } from "@repo/domain";
import { generateLinkId } from "./utils/link-id";

interface DraftIdentity {
  clientId: string;
  id?: string;
}
interface ConditionalDraft {
  conditions?: DraftEnableWhen[];
  conditionBehavior?: "any" | "all";
}
export type DraftMemberReference = { kind: "Persisted"; id: string } | { kind: "Temporary"; clientRef: string };
export type DraftConditionValue =
  | Exclude<QuestionnaireCondition["value"], { kind: "Choice" }>
  | { kind: "Choice"; option: DraftMemberReference };
export type DraftEnableWhen = import("@repo/domain").QuestionnaireFactCondition | DraftQuestionCondition;
export interface DraftQuestionCondition {
  question: DraftMemberReference;
  operator: QuestionnaireCondition["operator"];
  value: DraftConditionValue;
}
export type DraftAnswerOption = { id?: string; clientId: string; label: string; detail?: string; points?: number };
export type DraftAnswerValue =
  | Exclude<QuestionnaireAnswerValue, { kind: "Choice" }>
  | { kind: "Choice"; options: DraftAnswerOption[]; cardinality: "Single" | "Multiple"; allowFreeText: boolean };
export interface DraftSection extends DraftIdentity, ConditionalDraft {
  kind: "Section";
  label: string;
  showTogether?: boolean;
  items: DraftItem[];
}
export interface DraftQuestion extends DraftIdentity, ConditionalDraft {
  kind: "Question";
  label: string;
  help?: string;
  required: boolean;
  askYesNoFirst?: boolean;
  value: DraftAnswerValue;
}
export interface DraftDisplay extends DraftIdentity {
  kind: "Display";
  text: string;
}
export interface DraftRepeatingGroup extends DraftIdentity {
  kind: "RepeatingGroup";
  label: string;
  minOccurrences: number;
  items: (DraftQuestion | DraftDisplay)[];
}
export type DraftItem = DraftQuestion | DraftDisplay | DraftRepeatingGroup;
export type DraftNode = DraftSection | DraftItem;
export type BuilderItemType = "Section" | "RepeatingGroup" | "Display" | QuestionnaireAnswerValue["kind"];
export interface DraftBand {
  clientId: string;
  label: string;
  from: number;
  to?: number;
}
export interface DraftScore extends DraftIdentity {
  label: string;
  items: DraftMemberReference[];
  bands?: DraftBand[];
  calculation?: QuestionnaireScoreCalculation;
}
export interface QuestionnaireDraft {
  scores: DraftScore[];
  /** The Total is derived, so its bands are drafted apart from the subscores. */
  totalBands?: DraftBand[];
  url?: string;
  name?: string;
  description?: string;
  version?: number;
  sections: DraftSection[];
}

export const ITEM_TYPES: { value: BuilderItemType; label: string; description: string }[] = [
  { value: "Section", label: "Section", description: "A titled group of questionnaire items" },
  { value: "RepeatingGroup", label: "Repeating group", description: "A group the respondent may repeat" },
  { value: "Display", label: "Display", description: "Read-only text or instructions" },
  { value: "Text", label: "Text", description: "A text answer" },
  { value: "Integer", label: "Integer", description: "A whole-number answer" },
  { value: "Quantity", label: "Quantity", description: "A measured numeric answer" },
  { value: "Boolean", label: "Boolean", description: "A yes/no answer" },
  { value: "Date", label: "Date", description: "A calendar date" },
  { value: "Choice", label: "Choice", description: "One or more predefined options" },
  { value: "Attachment", label: "Attachment", description: "A file or document upload" },
  { value: "Url", label: "URL", description: "A web address" },
];

function bandsToDraft(bands: readonly { label: string; from: number; to?: number }[] | undefined): DraftBand[] {
  return (bands ?? []).map((band) => ({ ...band, clientId: generateLinkId() }));
}

function bandsInput(bands: DraftBand[] | undefined): { label: string; from: number; to?: number }[] {
  return (bands ?? []).map(({ label, from, to }) => ({ label, from, ...(to === undefined ? {} : { to }) }));
}

/**
 * What a define or revise sends: every band list is explicit, so an emptied one
 * clears. Drop Total bands whenever the draft has no implicit Total, including
 * performance measures mixing choice points and numeric values.
 */
export function scoresInput(draft: QuestionnaireDraft, performance = false) {
  const hasTotal = draftTotalScore(draft.sections, performance, draft.scores);
  return {
    scores: draft.scores.map(({ clientId, bands, ...score }) => ({
      ...score,
      clientRef: clientId,
      bands: bandsInput(bands),
    })),
    totalBands: hasTotal ? bandsInput(draft.totalBands) : [],
  };
}

export function definitionToDraft(definition: QuestionnaireDefinitionDto): QuestionnaireDraft {
  return {
    url: definition.url,
    name: definition.name,
    description: definition.description,
    version: definition.version,
    totalBands: bandsToDraft(definition.scores.find((score) => score.id === TOTAL_SCORE_ID)?.bands),
    scores: definition.scores
      .filter((score) => score.id !== TOTAL_SCORE_ID)
      .map((score) => ({
        id: score.id,
        clientId: score.id,
        label: score.label,
        items: score.itemIds.map((id) => ({ kind: "Persisted" as const, id })),
        bands: bandsToDraft(score.bands),
        ...(score.calculation ? { calculation: score.calculation } : {}),
      })),
    sections: definition.sections.map((section) => ({
      ...section,
      kind: "Section",
      clientId: section.id,
      conditions: section.conditions?.map(conditionToDraft),
      items: section.items.map(itemToDraft),
    })),
  };
}

function itemToDraft(item: QuestionnaireDefinitionDto["sections"][number]["items"][number]): DraftItem {
  if (item.kind === "RepeatingGroup")
    return { ...item, clientId: item.id, items: item.items.map(itemToDraft) as (DraftQuestion | DraftDisplay)[] };
  if (item.kind === "Question")
    return {
      id: item.id,
      kind: item.kind,
      label: item.label,
      ...(item.help === undefined ? {} : { help: item.help }),
      required: item.required,
      ...(item.askYesNoFirst ? { askYesNoFirst: true } : {}),
      clientId: item.id,
      value:
        item.value.kind === "Choice"
          ? {
              ...item.value,
              options: item.value.options.map((option) => ({ ...option, clientId: option.id })),
            }
          : item.value,
      ...(item.conditions ? { conditions: item.conditions.map(conditionToDraft) } : {}),
      ...(item.conditionBehavior ? { conditionBehavior: item.conditionBehavior } : {}),
    };
  return { ...item, clientId: item.id };
}

function conditionToDraft(condition: EnablementCondition): DraftEnableWhen {
  if ("fact" in condition) return { ...condition };
  return {
    question: { kind: "Persisted", id: condition.question },
    operator: condition.operator,
    value:
      condition.value.kind === "Choice"
        ? { kind: "Choice", option: { kind: "Persisted", id: condition.value.optionId } }
        : condition.value,
  };
}

/** Draft members use client identity until their first save. */
export function draftTotalScore(
  sections: readonly DraftSection[],
  performance: boolean,
  scores: readonly DraftScore[],
) {
  return implicitTotalScore(
    sections.map((section) => ({ items: section.items.map((item) => ({ ...item, id: item.clientId })) })),
    performance,
    scores,
  );
}
